// Shared by the printed Sales invoice (Sales.tsx InvoiceDocument) and the
// printed Consignment (components/ConsignmentDocument.tsx): A4 sheet sizes,
// the row pagination, logo / footer image sizes and the amount in words.

// Printed logo size (Invoice Options › Logo Size). Height is 70% of width.
export const LOGO_W_DEFAULT = 120;
export const LOGO_W_MIN = 60;
export const LOGO_W_MAX = 260;
export const LOGO_RATIO = 0.7;
// Footer image height (Invoice Options › Footer Image).
export const FOOTER_IMG_H_DEFAULT = 96;
export const FOOTER_IMG_H_MIN = 40;
export const FOOTER_IMG_H_MAX = 160;


// The invoice is laid out as real A4 sheets (794×1123px = A4 at 96dpi, the
// same size Print uses). A hidden copy of the whole document is measured,
// then the rows are split so every sheet repeats the header (logo, company,
// invoice facts, Bill To / Ship To, terms grid, table head) and only the
// last sheet carries the totals, bank details and the footer (customer memo
// left, footer image right) pinned to its bottom edge. Keep SHEET_H /
// SHEET_PAD_Y in step with .invoice-doc-sheet in index.css.
export const SHEET_H     = 1123;
export const SHEET_PAD_Y = 56;
export const SHEET_SLACK = 6; // rounding headroom so a sheet never spills


export function paginateRows(root: HTMLElement, rowCount: number): number[][] | null {
  const h = (sel: string) => {
    const el = root.querySelector<HTMLElement>(sel);
    return el ? el.getBoundingClientRect().height : 0;
  };
  if (root.offsetHeight === 0) return null; // hidden (e.g. while printing)
  const table   = root.querySelector<HTMLElement>('.invoice-doc-table');
  const tableMb = table ? parseFloat(getComputedStyle(table).marginBottom) || 0 : 0;
  const rowHs   = Array.from(root.querySelectorAll<HTMLElement>('[data-m-row]')).map(r => r.getBoundingClientRect().height);
  if (rowHs.length !== rowCount) return null;

  const room    = SHEET_H - 2 * SHEET_PAD_Y - SHEET_SLACK - h('[data-m="top"]') - h('[data-m="thead"]') - tableMb;
  const tfootH  = h('[data-m="tfoot"]');
  const tailH   = h('[data-m="end"]') + h('[data-m="footer"]') + 16; // bank list + words + footer

  const pages: number[][] = [[]];
  let used = 0;
  rowHs.forEach((rh, i) => {
    if (used + rh > room && pages[pages.length - 1].length > 0) { pages.push([]); used = 0; }
    pages[pages.length - 1].push(i);
    used += rh;
  });
  // The Total row always stays with the last rows (one row moves to a new
  // sheet only if even the Total row doesn't fit). Bank details + footer
  // follow on the same sheet when there's room; otherwise they get a sheet
  // of their own with no table (marked by an empty [] entry), so earlier
  // sheets stay full and the footer is never cut.
  const last = pages[pages.length - 1];
  let lastUsed = last.reduce((sum, i) => sum + rowHs[i], 0);
  if (lastUsed + tfootH > room && last.length > 1) {
    const moved = [last.pop()!];
    pages.push(moved);
    lastUsed = rowHs[moved[0]];
  }
  if (lastUsed + tfootH + tailH > room) pages.push([]);
  return pages;
}


// ── Amount in words — e.g. 6786.86 → "Six Thousand Seven Hundred
// Eighty-Six and Eighty-Six Cents", matching the reference invoice
// template's "Six Thousand Seven Hundred Eighty-Six and Eighty-Six Cents
// Only" line. Display only, never stored. Handles up to billions. ──
const NUM_ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const NUM_TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function threeDigitsToWords(n: number): string {
  const parts: string[] = [];
  if (n >= 100) { parts.push(`${NUM_ONES[Math.floor(n / 100)]} Hundred`); n %= 100; }
  if (n >= 20) { parts.push(NUM_TENS[Math.floor(n / 10)] + (n % 10 ? `-${NUM_ONES[n % 10]}` : '')); }
  else if (n > 0) { parts.push(NUM_ONES[n]); }
  return parts.join(' ');
}
function integerToWords(n: number): string {
  if (n === 0) return 'Zero';
  const groups: string[] = [];
  const scales = ['', 'Thousand', 'Million', 'Billion'];
  let scaleIdx = 0;
  while (n > 0) {
    const chunk = n % 1000;
    if (chunk > 0) groups.unshift(`${threeDigitsToWords(chunk)}${scales[scaleIdx] ? ` ${scales[scaleIdx]}` : ''}`);
    n = Math.floor(n / 1000);
    scaleIdx++;
  }
  return groups.join(' ');
}
export function numberToWords(amount: number): string {
  const whole = Math.floor(Math.abs(amount));
  const cents = Math.round((Math.abs(amount) - whole) * 100);
  const wholeWords = integerToWords(whole);
  return cents > 0 ? `${wholeWords} and ${threeDigitsToWords(cents)} Cents` : wholeWords;
}

